/**
 * CalendarSharedGroupPanel — S3 (Final PRD §22–30, §48, §50). Lives inside the existing shift drawer (no second board).
 * Create a group from this shift + other participant shifts of the day, add/remove members, assign/change workers through the
 * group, split coverage (handover), replacement, open coverage, 2:1 (a second assignment), per-slice operational ratios.
 * Member participant shifts are never rewritten here; the server owns all rules.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api, { fetchAllPages } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TrinitiTimePicker } from '@/components/triniti-datetime';
import { toast } from 'sonner';
import { Users, Plus, X, Split, UserPlus, Repeat } from 'lucide-react';
import { StaffSelectWithAvailability } from './StaffAvailabilityHelpers';
import { sliceLabel, groupErrorMessage, liveAssignments, hasTwoToOne, memberCandidates } from './sharedDeliveryRules';
import { participantAttendanceLine, outcomeLabel, clockState, clockLabel, attendanceErrorMessage } from './attendanceRules';   // S4
import { Clock } from 'lucide-react';

export function CalendarSharedGroupPanel({ shift, staff, onChanged, group: externalGroup, onReloadGroup, conflicts = [], readOnly = false, onOpenMember }) {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('roster.manage') && !readOnly;
  const canAssign = hasPermission('roster.assign') && !readOnly;
  const [ownGroup, setOwnGroup] = useState(null);
  const group = externalGroup !== undefined ? externalGroup : ownGroup;
  const [dayShifts, setDayShifts] = useState([]);
  const [memberPick, setMemberPick] = useState('');
  const [assign, setAssign] = useState({ worker_id: '', start_time: '', end_time: '', role: 'PRIMARY' });
  const [split, setSplit] = useState(null); // { assignment_id, handover_time, successor_worker_id }
  const [clock, setClock] = useState(null); // S4-7 { assignment_id, event, reason }
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (onReloadGroup) return onReloadGroup();
    if (!shift?.id) return null;
    try {
      const r = await api.get('/shared-delivery/groups', { params: { shift_id: shift.id } });
      const g = (r.data?.data || [])[0];
      if (g) {
        const full = await api.get(`/shared-delivery/groups/${g.id}`);
        setOwnGroup(full.data?.data || null);
      } else {
        setOwnGroup(null);
      }
    } catch { setOwnGroup(null); }
    return null;
  }, [shift?.id, onReloadGroup]);

  useEffect(() => { if (externalGroup === undefined) load(); }, [load, externalGroup]);

  useEffect(() => {
    if (!shift?.date || !canManage) return;
    let active = true;
    fetchAllPages('/roster/shifts', { start_date: shift.date, end_date: shift.date })
      .then((items) => { if (active) setDayShifts(items || []); })
      .catch(() => { if (active) setDayShifts([]); });
    return () => { active = false; };
  }, [shift?.date, canManage]);

  const candidates = useMemo(() => memberCandidates(Array.isArray(dayShifts) ? dayShifts : [], group).filter((s) => s.id !== shift?.id), [dayShifts, group, shift?.id]);
  const live = liveAssignments(group);

  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); await load(); onChanged && onChanged(); }
    catch (e) { toast.error(e?._attendance ? attendanceErrorMessage(e) : groupErrorMessage(e)); }
    finally { setBusy(false); }
  };

  if (!shift?.participant_id) return null;
  const memberIds = group?.member_shift_ids || [];
  const eligibilityShiftId = shift?.id || memberIds[0];   // UA-4-F9: one batch eligibility read per shift
  const conflictFor = (wid) => (conflicts || []).find((c) => String(c.worker_id) === String(wid));
  // C5: legacy "who is present when" (participant_times) folded in as read-only evidence — one shared-delivery surface.
  const legacyWindows = (shift.participant_times || []).filter((t) => t?.participant_id);

  return (
    <Card data-testid="shared-group-panel">
      <CardHeader className="pb-2 px-3 pt-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Users className="w-4 h-4" /> Coverage &amp; shared delivery
          {group && <Badge variant="outline" className="text-[10px] border-sky-300 text-sky-700" data-testid="shared-group-type">{group.delivery_type === 'SIL_HOUSE' ? 'SIL house' : 'Non-SIL shared'}</Badge>}
          {group && hasTwoToOne(group) && <Badge variant="outline" className="text-[10px] border-violet-300 text-violet-700" data-testid="shared-group-2to1">2:1</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 pt-0 space-y-2 text-xs">
        {legacyWindows.length > 1 && (
          <div className="rounded border border-dashed px-2 py-1 space-y-0.5" data-testid="legacy-presence-windows">
            <div className="text-[10px] text-muted-foreground">Presence windows on the legacy shared record (read-only evidence — delivery is managed through the group below)</div>
            {legacyWindows.map((t) => <div key={t.participant_id} className="tabular-nums">{t.start_time}–{t.end_time} · Participant #{String(t.participant_id).slice(-4)}</div>)}
          </div>
        )}
        {!group && (
          <div className="space-y-1.5" data-testid="shared-group-empty">
            <div className="text-muted-foreground">This participant shift is delivered on its own. Group it with another participant shift of the day to share workers (overlap inside the group is intentional and allowed).</div>
            {canManage && (
              <div className="flex gap-1.5 items-end">
                <div className="flex-1">
                  <Label className="text-[10px]">Participant shift to deliver together</Label>
                  <Select value={memberPick} onValueChange={setMemberPick}>
                    <SelectTrigger className="h-7 text-xs" data-testid="shared-group-create-pick"><SelectValue placeholder="Select a shift" /></SelectTrigger>
                    <SelectContent>
                      {candidates.length === 0 && <div className="px-2 py-1.5 text-[10px] text-muted-foreground">No other ungrouped participant shifts on this day.</div>}
                      {candidates.map((s) => <SelectItem key={s.id} value={s.id}>{s.start_time}–{s.end_time} · {s.participant_name || `Participant #${String(s.participant_id).slice(-4)}`}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button size="sm" className="h-7 text-xs" disabled={busy || !memberPick} data-testid="shared-group-create-btn"
                  onClick={() => run(() => api.post('/shared-delivery/groups', { member_shift_ids: [shift.id, memberPick] }), 'Shared delivery group created')}>
                  <Plus className="w-3 h-3 mr-1" /> Group
                </Button>
              </div>
            )}
          </div>
        )}

        {group && (
          <>
            <div className="text-[11px] text-muted-foreground">Shared window <span className="font-medium text-foreground tabular-nums">{group.window?.start_time}–{group.window?.end_time}</span>{group.window?.end_date !== group.window?.date && <> (ends {group.window?.end_date})</>}</div>
            <div className="space-y-1" data-testid="shared-group-members">
              <div className="font-medium">Participant shifts ({(group.members || []).length})</div>
              {(group.members || []).map((m) => (
                <div key={m.id} className="flex items-center justify-between rounded border bg-slate-50 px-2 py-1" data-testid={`shared-member-${m.id}`}>
                  <span className="tabular-nums">{m.start_time}–{m.end_time} · {onOpenMember && m.id !== shift.id
                    ? <button type="button" className="text-sky-800 hover:underline" onClick={() => onOpenMember(m.id)} data-testid={`shared-member-open-${m.id}`}>{m.participant_name || `Participant #${String(m.participant_id).slice(-4)}`}</button>
                    : (m.participant_name || `Participant #${String(m.participant_id).slice(-4)}`)}{m.id === shift.id ? ' (this shift)' : ''}</span>
                  {canManage && (
                    <Button size="sm" variant="ghost" className="h-5 w-5 p-0" disabled={busy} data-testid={`shared-member-remove-${m.id}`}
                      onClick={() => run(() => api.delete(`/shared-delivery/groups/${group.id}/members/${m.id}`), 'Shift removed from the group')}>
                      <X className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              ))}
              {canManage && (
                <div className="flex gap-1.5 items-end">
                  <div className="flex-1">
                    <Select value={memberPick} onValueChange={setMemberPick}>
                      <SelectTrigger className="h-7 text-xs" data-testid="shared-group-add-pick"><SelectValue placeholder="Add a participant shift" /></SelectTrigger>
                      <SelectContent>
                        {candidates.length === 0 && <div className="px-2 py-1.5 text-[10px] text-muted-foreground">No other ungrouped participant shifts on this day.</div>}
                        {candidates.map((s) => <SelectItem key={s.id} value={s.id}>{s.start_time}–{s.end_time} · {s.participant_name || `Participant #${String(s.participant_id).slice(-4)}`}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy || !memberPick} data-testid="shared-group-add-btn"
                    onClick={() => run(() => api.post(`/shared-delivery/groups/${group.id}/members`, { shift_id: memberPick }), 'Shift added to the group')}>
                    <Plus className="w-3 h-3 mr-1" /> Add
                  </Button>
                </div>
              )}
            </div>

            <div className="space-y-1" data-testid="shared-group-coverage">
              <div className="font-medium">Worker coverage</div>
              {live.length === 0 && <div className="text-muted-foreground" data-testid="shared-group-no-coverage">No worker assigned through the group yet (open coverage).</div>}
              {live.map((a) => (
                <div key={a.assignment_id} className={`rounded border px-2 py-1 flex items-center justify-between ${a.status === 'OPEN' ? 'border-amber-300 bg-amber-50' : conflictFor(a.worker_id) ? 'border-red-300 bg-red-50' : 'bg-slate-50'}`} data-testid={`shared-assignment-${a.assignment_id}`}>
                  <span className="tabular-nums">{a.start_time}–{a.end_time} · {a.status === 'OPEN' ? 'OPEN coverage' : (a.worker_name || `Worker #${String(a.worker_id).slice(-4)}`)}
                    {a.role !== 'PRIMARY' && <Badge variant="outline" className="ml-1 text-[9px]">{a.role}</Badge>}
                    {a.handover_at && <span className="text-[10px] text-muted-foreground"> · handover {a.end_time}</span>}
                    {conflictFor(a.worker_id) && <span className="block text-[10px] text-red-800" data-testid={`shared-assignment-conflict-${a.assignment_id}`}>{conflictFor(a.worker_id).reason}</span>}
                  </span>
                  {canAssign && (
                    <span className="flex gap-0.5">
                      {a.status === 'ASSIGNED' && (
                        <Button size="sm" variant="ghost" className="h-5 w-5 p-0" title="Split coverage / handover" disabled={busy} data-testid={`shared-assignment-split-${a.assignment_id}`}
                          onClick={() => setSplit({ assignment_id: a.assignment_id, handover_time: '', successor_worker_id: '' })}><Split className="w-3 h-3" /></Button>
                      )}
                      <Button size="sm" variant="ghost" className="h-5 w-5 p-0" title="Replacement worker" disabled={busy} data-testid={`shared-assignment-replace-${a.assignment_id}`}
                        onClick={() => setSplit({ assignment_id: a.assignment_id, replace: true, successor_worker_id: '' })}><Repeat className="w-3 h-3" /></Button>
                      <Button size="sm" variant="ghost" className="h-5 w-5 p-0 text-red-600" title="Remove coverage (becomes open)" disabled={busy} data-testid={`shared-assignment-remove-${a.assignment_id}`}
                        onClick={() => run(() => api.delete(`/shared-delivery/groups/${group.id}/assignments/${a.assignment_id}`), 'Coverage removed — participants keep their shifts')}><X className="w-3 h-3" /></Button>
                      {a.worker_id && clockState(a) !== 'CLOCKED_OUT' && (
                        <Button size="sm" variant="ghost" className="h-5 px-1 text-[10px]" title="Clock on behalf of the worker (reason required)" disabled={busy} data-testid={`shared-assignment-clock-${a.assignment_id}`}
                          onClick={() => setClock({ assignment_id: a.assignment_id, event: clockState(a) === 'CLOCKED_IN' ? 'clock-out' : 'clock-in', reason: '' })}>
                          <Clock className="w-3 h-3 mr-0.5" /> {clockState(a) === 'CLOCKED_IN' ? 'Out' : 'In'}
                        </Button>
                      )}
                    </span>
                  )}
                </div>
              ))}
              {live.some((a) => a.worker_id) && (
                <div className="text-[10px] text-muted-foreground" data-testid="shared-clock-states">
                  {live.filter((a) => a.worker_id).map((a) => <div key={a.assignment_id}>{a.worker_name || `Worker #${String(a.worker_id).slice(-4)}`}: {clockLabel(a)}</div>)}
                </div>
              )}
              {clock && (
                <div className="rounded border p-2 space-y-1.5" data-testid="shared-clock-editor">
                  <div className="font-medium">{clock.event === 'clock-in' ? 'Clock in' : 'Clock out'} on behalf of the worker (now)</div>
                  <Input value={clock.reason} onChange={(e) => setClock((c) => ({ ...c, reason: e.target.value }))} className="h-7 text-xs" placeholder="Reason (required)" data-testid="shared-clock-reason" />
                  <div className="flex justify-end gap-1.5">
                    <Button size="sm" variant="outline" className="h-6 text-xs" onClick={() => setClock(null)}>Cancel</Button>
                    <Button size="sm" className="h-6 text-xs" disabled={busy || !clock.reason.trim()} data-testid="shared-clock-save"
                      onClick={() => run(() => api.post(`/shared-delivery/groups/${group.id}/assignments/${clock.assignment_id}/${clock.event}`, { at: new Date().toISOString(), reason: clock.reason.trim() }).catch((e) => { throw Object.assign(e, { _attendance: true }); }),
                        clock.event === 'clock-in' ? 'Worker clocked in through the group' : 'Worker clocked out — timesheet generated').then(() => setClock(null))}>Save</Button>
                  </div>
                </div>
              )}
              {split && (
                <div className="rounded border p-2 space-y-1.5" data-testid="shared-split-editor">
                  <div className="font-medium">{split.replace ? 'Replacement worker' : 'Split coverage (handover)'}</div>
                  {!split.replace && (
                    <div><Label className="text-[10px]">Handover time</Label><TrinitiTimePicker value={split.handover_time} onChange={(v) => setSplit((s) => ({ ...s, handover_time: v }))} testId="shared-split-handover" /></div>
                  )}
                  <div>
                    <Label className="text-[10px]">{split.replace ? 'Worker' : 'Successor (leave empty for open coverage)'}</Label>
                    <StaffSelectWithAvailability eligibilityShiftId={eligibilityShiftId} staff={staff} value={split.successor_worker_id} onValueChange={(v) => setSplit((s) => ({ ...s, successor_worker_id: v }))} date={shift.date} startTime={shift.start_time} endTime={shift.end_time} testId="shared-split-worker" groupShiftIds={memberIds} blockAccidental />
                  </div>
                  <div className="flex justify-end gap-1.5">
                    <Button size="sm" variant="outline" className="h-6 text-xs" onClick={() => setSplit(null)}>Cancel</Button>
                    <Button size="sm" className="h-6 text-xs" disabled={busy || (split.replace ? !split.successor_worker_id : !split.handover_time)} data-testid="shared-split-save"
                      onClick={() => run(() => split.replace
                        ? api.post(`/shared-delivery/groups/${group.id}/assignments/${split.assignment_id}/replace`, { worker_id: split.successor_worker_id })
                        : api.post(`/shared-delivery/groups/${group.id}/assignments/${split.assignment_id}/split`, { handover_time: split.handover_time, successor_worker_id: split.successor_worker_id || null }),
                      split.replace ? 'Replacement assigned' : 'Coverage split').then(() => setSplit(null))}>
                      Save
                    </Button>
                  </div>
                </div>
              )}
              {canAssign && !split && (
                <div className="rounded border p-2 space-y-1.5" data-testid="shared-assign-editor">
                  <div className="grid grid-cols-2 gap-1.5">
                    <div><Label className="text-[10px]">From</Label><TrinitiTimePicker value={assign.start_time} onChange={(v) => setAssign((a) => ({ ...a, start_time: v }))} testId="shared-assign-start" /></div>
                    <div><Label className="text-[10px]">To</Label><TrinitiTimePicker value={assign.end_time} onChange={(v) => setAssign((a) => ({ ...a, end_time: v }))} testId="shared-assign-end" /></div>
                  </div>
                  <div className="grid grid-cols-[1fr_auto] gap-1.5 items-end">
                    <div><Label className="text-[10px]">Worker (empty = open coverage)</Label><StaffSelectWithAvailability eligibilityShiftId={eligibilityShiftId} staff={staff} value={assign.worker_id} onValueChange={(v) => setAssign((a) => ({ ...a, worker_id: v }))} date={shift.date} startTime={shift.start_time} endTime={shift.end_time} testId="shared-assign-worker" groupShiftIds={memberIds} blockAccidental /></div>
                    <div>
                      <Label className="text-[10px]">Role</Label>
                      <Select value={assign.role} onValueChange={(v) => setAssign((a) => ({ ...a, role: v }))}>
                        <SelectTrigger className="h-7 text-xs w-28" data-testid="shared-assign-role"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="PRIMARY">Primary</SelectItem><SelectItem value="SECOND">Second (2:1)</SelectItem></SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button size="sm" variant="outline" className="h-7 text-xs w-full" disabled={busy} data-testid="shared-assign-btn"
                    onClick={() => run(() => api.post(`/shared-delivery/groups/${group.id}/assignments`, { worker_id: assign.worker_id || null, start_time: assign.start_time || undefined, end_time: assign.end_time || undefined, role: assign.role }), assign.worker_id ? 'Worker assigned through the group' : 'Open coverage recorded')}>
                    <UserPlus className="w-3 h-3 mr-1" /> {assign.worker_id ? 'Assign through group' : 'Record open coverage'}
                  </Button>
                </div>
              )}
            </div>

            {(group.coverage_evidence || []).length > 0 && (
              <div className="space-y-0.5" data-testid="shared-group-attendance">
                <div className="font-medium">Participant attendance <span className="font-normal text-muted-foreground">(scheduled vs actual — recorded per participant, never inferred from a clock)</span></div>
                {(group.coverage_evidence || []).map((ev) => {
                  const line = participantAttendanceLine(ev);
                  return (
                    <div key={ev.shift_id} className="flex justify-between tabular-nums" data-testid={`shared-attendance-${ev.shift_id}`}>
                      <span>{ev.participant_name || `Participant #${String(ev.participant_id).slice(-4)}`} · {line.scheduled}</span>
                      <span className={line.status === 'UNRECORDED' ? 'text-muted-foreground' : line.status === 'NO_SHOW' ? 'text-red-700' : ''}>{line.actual}{ev.delivery_outcome ? ` · ${outcomeLabel(ev.delivery_outcome)}` : ''}</span>
                    </div>
                  );
                })}
                {(group.actual_ratio_slices || []).length > 0 && (
                  <div className="text-[10px] text-muted-foreground">Actual ratio: {(group.actual_ratio_slices || []).map(sliceLabel).join(' · ')}</div>
                )}
              </div>
            )}
            <div className="space-y-0.5" data-testid="shared-group-ratios">
              <div className="font-medium">Operational ratio by time slice <span className="font-normal text-muted-foreground">(delivery attribute — never alters the signed rate)</span></div>
              {(group.ratio_slices || []).map((s, i) => (
                <div key={i} className={`flex justify-between tabular-nums ${s.open_coverage ? 'text-amber-700' : ''}`} data-testid={`shared-ratio-slice-${i}`}>
                  <span>{sliceLabel(s)}</span><span className="text-muted-foreground">{s.participant_ids.length} participant{s.participant_ids.length === 1 ? '' : 's'} · {s.worker_ids.length} worker{s.worker_ids.length === 1 ? '' : 's'}</span>
                </div>
              ))}
            </div>
            {canManage && (
              <Button size="sm" variant="ghost" className="h-6 text-xs text-red-600" disabled={busy} data-testid="shared-group-dissolve"
                onClick={() => run(() => api.delete(`/shared-delivery/groups/${group.id}`), 'Group dissolved — participant shifts unchanged')}>Dissolve group</Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default CalendarSharedGroupPanel;
